import * as React from "react";
import apiClient from "@/api-client";
import { zodResolver } from "@hookform/resolvers/zod";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardHeader from "@mui/material/CardHeader";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import OutlinedInput from "@mui/material/OutlinedInput";
import Stack from "@mui/material/Stack";
import { EyeIcon } from "@phosphor-icons/react/dist/ssr/Eye";
import { EyeSlashIcon } from "@phosphor-icons/react/dist/ssr/EyeSlash";
import { Controller, useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { z as zod } from "zod";

import { paths } from "@/paths";
import { CenteredLayout } from "@/components/auth/centered-layout";
import { RouterLink } from "@/components/core/link";
import { DynamicLogo } from "@/components/core/logo";
import { CustomFormHelperError } from "@/components/customFormHelper/custom-form-helper-error";
import { useEffect } from "react";
import { Link } from "@mui/material";

const schema = zod
	.object({
		newPassword: zod.string().min(1, { message: "Password is required" }),
		confirmPassword: zod.string().min(1, { message: "Confirm password is required" }),
	})
	.refine(
		(data) => data.newPassword === data.confirmPassword,
		{ message: "Passwords do not match", path: ["confirmPassword"] }
	);

type Values = zod.infer<typeof schema>;

export function ResetPassword(): React.JSX.Element {
	const navigate = useNavigate();
	const [showPassword, setShowPassword] = React.useState(false);
	const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);

	const [searchParams] = useSearchParams();
	const token = searchParams.get("token");

	const {
		control,
		handleSubmit,
		formState: { errors, isSubmitting },
	} = useForm<Values>({ defaultValues: { newPassword: "", confirmPassword: "" }, resolver: zodResolver(schema) });

  useEffect(() => {
    localStorage.removeItem("token");
  }, []);

	const onSubmit = async (data: Values) => {
		try {
			await apiClient.post("/email-verification/reset-password", {
				newPassword: data.newPassword,
				token: token,
			});

			toast.success("Password reset successfully");

			navigate("/sign-in");
		} catch (error: any) {
      const errorMessages = error?.response?.data?.message;

      if (!Array.isArray(errorMessages)) {
        toast.error(errorMessages);
        return;
      }

			const errorMessage = errorMessages[0];

			toast.error(
				errorMessage.split(" ")[0].split(".").pop() + " " + errorMessage?.split(" ").slice(1).join(" ") ||
					"Something went wrong!"
			);
		}
	};

	return (
		<React.Fragment>
			<CenteredLayout>
				<Stack spacing={4}>
					<div>
						<Box
							component={RouterLink}
							href={paths.home}
							sx={{ display: "flex", fontSize: 0, justifyContent: "center" }}
						>
							<DynamicLogo colorDark="light" colorLight="dark" height={42} width={222} />
						</Box>
					</div>
					<Card>
						<CardHeader title="Reset password" subheader="Enter your new password" />
						<CardContent>
							<form onSubmit={handleSubmit(onSubmit)}>
								<Stack spacing={2}>
									<Stack spacing={2}>
										<Controller
											control={control}
											name="newPassword"
											render={({ field }) => (
												<FormControl error={Boolean(errors.newPassword)}>
													<InputLabel>Password</InputLabel>
													<OutlinedInput
														{...field}
														endAdornment={
															showPassword ? (
																<EyeIcon
																	cursor="pointer"
																	fontSize="var(--icon-fontSize-md)"
																	onClick={(): void => {
																		setShowPassword(false);
																	}}
																/>
															) : (
																<EyeSlashIcon
																	cursor="pointer"
																	fontSize="var(--icon-fontSize-md)"
																	onClick={(): void => {
																		setShowPassword(true);
																	}}
																/>
															)
														}
														type={showPassword ? "text" : "password"}
													/>
													{errors.newPassword ? <CustomFormHelperError text={errors.newPassword.message || ""} /> : null}
												</FormControl>
											)}
										/>
										<Controller
											control={control}
											name="confirmPassword"
											render={({ field }) => (
												<FormControl error={Boolean(errors.confirmPassword)}>
													<InputLabel>Confirm Password</InputLabel>
													<OutlinedInput
														{...field}
														endAdornment={
															showConfirmPassword ? (
																<EyeIcon
																	cursor="pointer"
																	fontSize="var(--icon-fontSize-md)"
																	onClick={(): void => {
																		setShowConfirmPassword(false);
																	}}
																/>
															) : (
																<EyeSlashIcon
																	cursor="pointer"
																	fontSize="var(--icon-fontSize-md)"
																	onClick={(): void => {
																		setShowConfirmPassword(true);
																	}}
																/>
															)
														}
														type={showConfirmPassword ? "text" : "password"}
													/>
													{errors.confirmPassword ? (
														<CustomFormHelperError text={errors.confirmPassword.message || ""} />
													) : null}
												</FormControl>
											)}
										/>

										<Button type="submit" variant="contained" loading={isSubmitting}>
											Reset Password
										</Button>
									</Stack>

                  <div>
										<Link variant="subtitle2" onClick={() => navigate("/sign-in")}>
											Back to sign in
										</Link>
									</div>
								</Stack>
							</form>
						</CardContent>
					</Card>
				</Stack>
			</CenteredLayout>
		</React.Fragment>
	);
}
